/**
 * Client-only scene cache for full-scene restore after a page reload.
 *
 * Heavy mesh payloads (typed-array buffers) live in IndexedDB — too large for
 * localStorage. A small synchronous index is mirrored into localStorage so
 * callers (e.g. `useBkScanAutoLoad`, the resume hook) can cheaply answer
 * "is there a cached scene for this order+step?" without awaiting IndexedDB.
 *
 * Everything is keyed by `orderKey` (`${sales_order}:${customer}`) so different
 * orders never collide, and per-step so resuming lands on the exact step mesh.
 *
 * All functions are defensive: storage failures (private mode, quota, no IDB)
 * degrade to "no cache" rather than throwing into the render path.
 */

const DB_NAME = 'benx-scene-cache';
const DB_VERSION = 1;
const STORE = 'steps';
const INDEX_KEY = 'bk_scene_cache_index_v1';

/** `${orderKey}::${stepId}` — the IndexedDB primary key for a step scene. */
function sceneKey(orderKey, stepId) {
  return `${orderKey}::${stepId}`;
}

// ---------------------------------------------------------------------------
// Synchronous localStorage index (existence + resume metadata)
// ---------------------------------------------------------------------------

function readIndex() {
  try {
    const raw = localStorage.getItem(INDEX_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeIndex(index) {
  try {
    localStorage.setItem(INDEX_KEY, JSON.stringify(index));
  } catch (e) {
    console.warn('[SceneCache] index write failed', e);
  }
}

/** Synchronous: does a cached scene exist for this order+step? */
export function hasCachedStep(orderKey, stepId) {
  if (!orderKey || !stepId) return false;
  const entry = readIndex()[orderKey];
  return !!entry?.steps?.[stepId];
}

/** Synchronous resume metadata for an order (stepCompletion, intent, currentStep). */
export function getResumeMeta(orderKey) {
  if (!orderKey) return null;
  return readIndex()[orderKey] ?? null;
}

/** Record that a step scene is cached, plus the workflow resume metadata. */
function markStepCached(orderKey, stepId, { stepCompletion, intent, currentStep } = {}) {
  const index = readIndex();
  const entry = index[orderKey] ?? { steps: {} };
  entry.steps = { ...entry.steps, [stepId]: Date.now() };
  if (stepCompletion) entry.stepCompletion = stepCompletion;
  if (intent) entry.bkMainProcessIntent = intent;
  if (currentStep) entry.currentStep = currentStep;
  entry.updatedAt = Date.now();
  index[orderKey] = entry;
  writeIndex(index);
}

// ---------------------------------------------------------------------------
// IndexedDB access
// ---------------------------------------------------------------------------

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  }).catch((e) => {
    dbPromise = null;
    throw e;
  });
  return dbPromise;
}

function idbRequest(mode, run) {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const store = tx.objectStore(STORE);
        const req = run(store);
        tx.oncomplete = () => resolve(req?.result);
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      }),
  );
}

/**
 * Persist a step scene (serialized mesh + session snapshot) and update the
 * synchronous index. Never throws — resolves false on failure.
 */
export function putStepScene(orderKey, stepId, record, resumeMeta = {}) {
  if (!orderKey || !stepId || !record) return Promise.resolve(false);
  return idbRequest('readwrite', (store) =>
    store.put({ orderKey, stepId, savedAt: Date.now(), ...record }, sceneKey(orderKey, stepId)),
  )
    .then(() => {
      markStepCached(orderKey, stepId, resumeMeta);
      return true;
    })
    .catch((e) => {
      console.warn('[SceneCache] putStepScene failed', e);
      return false;
    });
}

/** Read a cached step scene, or null if absent / unavailable. */
export function getStepScene(orderKey, stepId) {
  if (!orderKey || !stepId) return Promise.resolve(null);
  return idbRequest('readonly', (store) => store.get(sceneKey(orderKey, stepId)))
    .then((v) => v ?? null)
    .catch((e) => {
      console.warn('[SceneCache] getStepScene failed', e);
      return null;
    });
}

/**
 * Forget the cached scenes for specific steps.
 *
 * The counterpart to {@link putStepScene}, and the reason a backward revert
 * sticks: the in-memory checkpoint pools are cleared when work is discarded, but
 * this record outlives them. Left behind, it is picked up again the next time the
 * step is entered (or after a reload) and reinstates the mesh, tool session and
 * pre-apply snapshots the user just discarded.
 *
 * Never throws — resolves false on failure.
 *
 * @param {string} orderKey
 * @param {string[]} stepIds
 */
export function deleteStepScenes(orderKey, stepIds) {
  const ids = [...new Set((stepIds ?? []).filter(Boolean))];
  if (!orderKey || ids.length === 0) return Promise.resolve(false);

  const index = readIndex();
  const entry = index[orderKey];
  if (entry?.steps) {
    for (const stepId of ids) delete entry.steps[stepId];
    entry.updatedAt = Date.now();
    writeIndex(index);
  }

  return idbRequest('readwrite', (store) => {
    let req = null;
    for (const stepId of ids) req = store.delete(sceneKey(orderKey, stepId));
    return req;
  })
    .then(() => true)
    .catch((e) => {
      console.warn('[SceneCache] deleteStepScenes failed', e);
      return false;
    });
}

/**
 * Patch the synchronous resume metadata without writing a scene.
 *
 * A revert resets the completion flags for the steps it discarded; the index
 * still holds the flags from before, and `getResumeMeta` merges those back on the
 * next reload — re-opening steps whose work no longer exists.
 *
 * @param {string} orderKey
 * @param {{ stepCompletion?: object, intent?: string, currentStep?: string }} patch
 */
export function updateResumeMeta(orderKey, { stepCompletion, intent, currentStep } = {}) {
  if (!orderKey) return;
  const index = readIndex();
  const entry = index[orderKey];
  if (!entry) return;
  if (stepCompletion) entry.stepCompletion = stepCompletion;
  if (intent) entry.bkMainProcessIntent = intent;
  if (currentStep) entry.currentStep = currentStep;
  entry.updatedAt = Date.now();
  writeIndex(index);
}

/** Drop every cached step for an order (Start-from-scratch, order reset). */
export function clearOrderCache(orderKey) {
  if (!orderKey) return Promise.resolve(false);
  const index = readIndex();
  if (index[orderKey]) {
    delete index[orderKey];
    writeIndex(index);
  }
  const prefix = `${orderKey}::`;
  return idbRequest('readwrite', (store) => {
    const cursorReq = store.openKeyCursor();
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (!cursor) return;
      if (String(cursor.key).startsWith(prefix)) store.delete(cursor.key);
      cursor.continue();
    };
    return cursorReq;
  })
    .then(() => true)
    .catch((e) => {
      console.warn('[SceneCache] clearOrderCache failed', e);
      return false;
    });
}
